import {
    useCallback,
    useMemo,
    useRef,
    useState,
    type FocusEvent,
    type FocusEventHandler,
} from "react";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import type {
    PickerItemRegistration,
    PickerQueryContextValue,
    PickerRootContextValue,
} from "./PickerContext";
import { usePickerFocusLeave } from "./usePickerFocusLeave";
import type { usePickerSelection } from "./usePickerSelection";
import { useShallowStable } from "./usePickerStable";
import type {
    PickerChevron,
    PickerClassNames,
    PickerForcedState,
    PickerItemValue,
    PickerParentSelection,
    PickerRootKind,
    PickerStatus,
    PickerTypedCommitReason,
} from "./Picker.types";

export interface UsePickerRootContextValueOptions<T extends PickerItemValue> {
    kind: PickerRootKind;
    isMultiple: boolean;
    isTextValue: boolean;
    disabled: boolean;
    isInvalid: boolean;
    selectableParents: boolean;
    parentSelection: PickerParentSelection;
    cascadeDescendants?: ReadonlyMap<T, readonly T[]>;
    indicatedDescendants?: ReadonlyMap<T, readonly T[]>;
    exclusiveSiblings?: ReadonlyMap<T, readonly T[]>;
    selection: ReturnType<typeof usePickerSelection<T>>;
    /** Forwarded registration of a level's rows into the top root's registry. */
    chainRegisterItem?: (registration: PickerItemRegistration) => void;
    classNames: PickerClassNames;
    hasPopupSearch: boolean;
    isInline: boolean;
    openPopup: () => void;
    closePopup: () => void;
    handleInputValueChange: (query: string, reason?: string) => void;
    isPopupOpen: boolean;
    returnFocusOnCloseRef: { current: boolean };
    forcedState?: PickerForcedState;
    chevron: PickerChevron;
    explicitChevron?: PickerChevron;
    /** The chain this root's trigger and popups are marked with. */
    chainId: string;
    onBeforeTriggerBlur?: (event: FocusEvent<HTMLElement>) => void;
    onBlur?: FocusEventHandler<HTMLElement>;
    hasMore: boolean;
    loadMoreStatus: PickerStatus;
    onLoadMore?: () => void;
}

/**
 * The memoised `PickerRootContext` value of a list root: selection state and
 * callbacks, the trigger anchor and blur composition, and the search autofocus
 * flag. Kept referentially stable so nested levels do not re-render on every
 * root render: consumer handlers are read through refs and an inline
 * `classNames` literal is compared shallowly.
 */
export const usePickerRootContextValue = <T extends PickerItemValue>({
    kind,
    isMultiple,
    isTextValue,
    disabled,
    isInvalid,
    selectableParents,
    parentSelection,
    cascadeDescendants,
    indicatedDescendants,
    exclusiveSiblings,
    selection,
    chainRegisterItem,
    classNames,
    hasPopupSearch,
    isInline,
    openPopup,
    closePopup,
    handleInputValueChange,
    isPopupOpen,
    returnFocusOnCloseRef,
    forcedState,
    chevron,
    explicitChevron,
    chainId,
    onBeforeTriggerBlur,
    onBlur,
    hasMore,
    loadMoreStatus,
    onLoadMore,
}: UsePickerRootContextValueOptions<T>): PickerRootContextValue => {
    // `null` marks input-as-trigger roots, whose popup must not steal focus.
    const searchAutoFocusRef = useRef<boolean | null>(
        hasPopupSearch ? true : null,
    );
    const [hasInlineSearch, setHasInlineSearch] = useState(false);
    const [triggerAnchor, setTriggerAnchor] = useState<HTMLElement | null>(
        null,
    );
    const { handleTriggerBlur, handlePopupBlur, handlePopupFocus } =
        usePickerFocusLeave({ chainId, onBeforeTriggerBlur, onBlur });
    const stableClassNames = useShallowStable(classNames);
    // A level's rows register with the level and with the top root, so the
    // chain can label a leaf the top root never rendered itself.
    const { registerItem: registerOwnItem } = selection;
    const registerItem = useCallback(
        (registration: PickerItemRegistration) => {
            registerOwnItem(registration);
            chainRegisterItem?.(registration);
        },
        [registerOwnItem, chainRegisterItem],
    );
    const setInputValue = useCallback(
        (text: string) => handleInputValueChange(text, "input-change"),
        [handleInputValueChange],
    );
    const onLoadMoreRef = useLatestRef(onLoadMore);
    const handleLoadMore = useCallback(
        () => onLoadMoreRef.current?.(),
        [onLoadMoreRef],
    );
    const {
        selectedValues,
        isSelected,
        setSelectedValues,
        isValueDisabled,
        selectionLabel,
        selectionNode,
    } = selection;

    // Base UI trims keyboard navigation to its `items` array, which would drop
    // the footer row; rows are rendered and filtered here instead.
    return useMemo<PickerRootContextValue>(
        () => ({
            kind,
            isMultiple,
            isTextValue,
            isDisabled: disabled,
            isInvalid,
            isParentSelectable: selectableParents,
            parentSelection,
            cascadeDescendants,
            indicatedDescendants,
            exclusiveSiblings,
            selectedValues,
            isSelected,
            setSelectedValues,
            registerItem,
            isValueDisabled,
            selectionLabel,
            selectionNode,
            isPopupOpen,
            classNames: stableClassNames,
            chainId,
            onTriggerBlur: handleTriggerBlur,
            onPopupFocus: handlePopupFocus,
            onPopupBlur: handlePopupBlur,
            openPopup,
            closePopup,
            setInputValue: kind === "combobox" ? setInputValue : undefined,
            searchAutoFocusRef,
            isInline,
            hasInlineSearch,
            setHasInlineSearch,
            returnFocusOnCloseRef,
            triggerAnchor,
            setTriggerAnchor,
            forcedState,
            chevron,
            explicitChevron,
            hasMore,
            loadMoreStatus,
            onLoadMore: handleLoadMore,
        }),
        [
            kind,
            isMultiple,
            isTextValue,
            disabled,
            isInvalid,
            selectableParents,
            parentSelection,
            cascadeDescendants,
            indicatedDescendants,
            exclusiveSiblings,
            selectedValues,
            isSelected,
            setSelectedValues,
            registerItem,
            isValueDisabled,
            selectionLabel,
            selectionNode,
            isPopupOpen,
            stableClassNames,
            chainId,
            handleTriggerBlur,
            handlePopupFocus,
            handlePopupBlur,
            openPopup,
            closePopup,
            setInputValue,
            isInline,
            hasInlineSearch,
            triggerAnchor,
            returnFocusOnCloseRef,
            forcedState,
            chevron,
            explicitChevron,
            hasMore,
            loadMoreStatus,
            handleLoadMore,
        ],
    );
};

/** The memoised `PickerQueryContext` value: everything that changes with the typed text. */
export const usePickerQueryContextValue = ({
    query,
    filterQuery,
    searchText,
    topQuery,
    isPopupHidden,
    commitTypedText,
}: {
    query: string;
    filterQuery: string;
    searchText: string;
    topQuery: string;
    isPopupHidden: boolean;
    commitTypedText?: (reason: PickerTypedCommitReason) => boolean;
}): PickerQueryContextValue =>
    useMemo(
        () => ({
            inputValue: query,
            filterQuery,
            searchText,
            topQuery,
            isPopupHidden,
            commitTypedText,
        }),
        [
            query,
            filterQuery,
            searchText,
            topQuery,
            isPopupHidden,
            commitTypedText,
        ],
    );
